import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { GetSleepScreen, UpdateSleepScreen } from '../../libs/GraphQL';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import EditCascadeWarningModal from './EditCascadeWarningModal';
import {
  formatDate,
  CLOUDINARY_PREFIX,
  isCloudinaryVideoUrl,
  firstGraphQLError,
} from './sleepScreenHelpers';
import styles from './SleepScreenEdit.module.css';

const SleepScreenEdit = () => {
  const { sleepScreenId } = useParams();
  const history = useHistory();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) =>
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const [pendingLocation, setPendingLocation] = useState(null);
  const unblockRef = useRef(null);
  const discardingRef = useRef(false);
  const [showCascadeWarning, setShowCascadeWarning] = useState(false);
  // Snapshot of the values being submitted when the cascade modal opens, so
  // the diff in the modal stays frozen even if the user re-edits the form
  // while the modal is up.
  const [pendingSave, setPendingSave] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const result = await GetSleepScreen(sleepScreenId);
        if (cancelled) return;
        if (!result) {
          setNotFound(true);
        } else {
          setEntry(result);
          setDisplayName(result.displayName || '');
          setDescription(result.description || '');
          setVideoUrl(result.videoUrl || '');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('getSleepScreen failed:', err);
        toast.error('Failed to load sleep screen.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
    };
  }, [sleepScreenId]);

  const changedFields = useMemo(() => {
    if (!entry) return [];
    const diffs = [];
    if (displayName.trim() !== (entry.displayName || '').trim()) {
      diffs.push('displayName');
    }
    if (description.trim() !== (entry.description || '').trim()) {
      diffs.push('description');
    }
    if (videoUrl.trim() !== (entry.videoUrl || '').trim()) {
      diffs.push('videoUrl');
    }
    return diffs;
  }, [entry, displayName, description, videoUrl]);

  const isDirty = changedFields.length > 0;

  const validate = () => {
    const errors = {};
    if (!displayName.trim()) {
      errors.displayName = 'Display name is required.';
    }
    if (!videoUrl.trim()) {
      errors.videoUrl = 'Video URL is required.';
    } else if (!isCloudinaryVideoUrl(videoUrl)) {
      errors.videoUrl = `Must be a Cloudinary video URL (starts with ${CLOUDINARY_PREFIX}).`;
    }
    return errors;
  };

  const closeCascadeModal = () => {
    setShowCascadeWarning(false);
    setPendingSave(null);
  };

  const performSave = async () => {
    setSubmitting(true);
    try {
      const input = { sleepScreenId: entry.sleepScreenId };
      if (changedFields.includes('displayName')) {
        input.displayName = displayName.trim();
      }
      if (changedFields.includes('description')) {
        input.description = description.trim();
      }
      if (changedFields.includes('videoUrl')) {
        input.videoUrl = videoUrl.trim();
      }
      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) {
        input.updatedBy = userEmail;
      }
      await UpdateSleepScreen(input);
      closeCascadeModal();
      history.push(
        `/sleep-screens/${encodeURIComponent(entry.sleepScreenId)}`,
        { justUpdated: true, fieldsChanged: changedFields.length },
      );
    } catch (err) {
      const firstError = firstGraphQLError(err);
      const errorType = firstError.errorType;
      if (errorType === 'SLEEP_SCREEN_NOT_FOUND') {
        toast.error('This entry no longer exists.');
        history.push('/sleep-screens');
      } else if (errorType === 'INVALID_CLOUDINARY_URL') {
        setFieldErrors({
          videoUrl:
            (firstError && firstError.message) || 'Invalid Cloudinary URL.',
        });
        closeCascadeModal();
      } else if (errorType === 'INVALID_DISPLAY_NAME') {
        setFieldErrors({
          displayName:
            (firstError && firstError.message) || 'Display name is invalid.',
        });
        closeCascadeModal();
      } else {
        toast.error('Something went wrong, please try again.');
        console.error('updateSleepScreen failed:', err);
        closeCascadeModal();
      }
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isDirty || submitting || showCascadeWarning) return;
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (changedFields.includes('videoUrl')) {
      setPendingSave({
        description: description.trim(),
        videoUrl: videoUrl.trim(),
      });
      setShowCascadeWarning(true);
      return;
    }
    performSave();
  };

  const handleDiscard = () => {
    discardingRef.current = true;
    history.push(`/sleep-screens/${encodeURIComponent(sleepScreenId)}`);
  };

  useEffect(() => {
    // Skip the unsaved-changes Prompt while the cascade-warning modal is open
    // so we don't stack two modals on top of each other if the user hits Back.
    if (!isDirty || submitting || showCascadeWarning) return undefined;
    unblockRef.current = history.block((nextLocation) => {
      if (discardingRef.current) {
        discardingRef.current = false;
        return undefined;
      }
      setPendingLocation(nextLocation);
      return false;
    });
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isDirty, submitting, showCascadeWarning, history]);

  const handleConfirmLeave = () => {
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    const target = pendingLocation;
    setPendingLocation(null);
    if (target) history.push(target.pathname + (target.search || ''));
  };

  const handleCancelLeave = () => setPendingLocation(null);

  if (loading) {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link>
        </div>
        <div className={styles.stateBlock}>Loading…</div>
      </main>
    );
  }

  if (notFound || !entry) {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link> &gt; not found
        </div>
        <div className={styles.stateBlock}>
          No sleep screen with id <code>{sleepScreenId}</code> exists.
        </div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <Modal
        show={!!pendingLocation}
        onHide={handleCancelLeave}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Unsaved changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes on this entry. Leave without saving?
        </Modal.Body>
        <Modal.Footer>
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.discardButton}
              onClick={handleConfirmLeave}
            >
              leave anyway
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleCancelLeave}
            >
              keep editing
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      <div className={styles.breadcrumb}>
        <Link to="/sleep-screens">Library</Link> &gt;{' '}
        <Link to={`/sleep-screens/${encodeURIComponent(entry.sleepScreenId)}`}>
          {entry.displayName || entry.sleepScreenId}
        </Link>
      </div>

      <h1>Library Entry · editing</h1>

      <form onSubmit={handleSubmit} className={styles.editForm}>
        <section className={styles.topRow}>
          <SleepScreenVideoPreview url={videoUrl || entry.videoUrl} />
          <div className={styles.topMeta}>
            <div className={styles.id}>{entry.sleepScreenId}</div>
            {isDirty && (
              <span className={styles.unsavedTag}>UNSAVED CHANGES</span>
            )}
            <div className={styles.timestamps}>
              created {formatDate(entry.createdAt, entry.createdBy)} · last edit{' '}
              {formatDate(entry.updatedAt)}
            </div>
          </div>
        </section>

        <section className={styles.detailsCard}>
          <h2>Details</h2>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="seName">
              Name<span className={styles.required}>*</span>
            </label>
            <input
              id="seName"
              type="text"
              className={`${styles.input} ${
                fieldErrors.displayName ? styles.inputError : ''
              }`}
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                clearFieldError('displayName');
              }}
              disabled={submitting}
            />
            {fieldErrors.displayName && (
              <span className={styles.fieldError}>
                {fieldErrors.displayName}
              </span>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="seId">
              ID{' '}
              <span role="img" aria-label="locked">
                🔒
              </span>
            </label>
            <input
              id="seId"
              type="text"
              className={styles.lockedInput}
              value={entry.sleepScreenId}
              disabled
            />
            <span className={styles.hint}>
              auto-derived; cannot change after create
            </span>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="seDescription">
              Description
            </label>
            <textarea
              id="seDescription"
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="seVideoUrl">
              Video URL<span className={styles.required}>*</span>
            </label>
            <input
              id="seVideoUrl"
              type="text"
              className={`${styles.input} ${
                fieldErrors.videoUrl ? styles.inputError : ''
              }`}
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value);
                clearFieldError('videoUrl');
              }}
              disabled={submitting}
            />
            {fieldErrors.videoUrl && (
              <span className={styles.fieldError}>{fieldErrors.videoUrl}</span>
            )}
          </div>
        </section>

        <footer className={styles.footer}>
          <span className={styles.fieldsCounter}>
            {changedFields.length === 0
              ? 'no changes'
              : `${changedFields.length} ${
                  changedFields.length === 1 ? 'field' : 'fields'
                } changed`}
          </span>
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.discardButton}
              onClick={handleDiscard}
              disabled={submitting}
            >
              discard
            </button>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={submitting || showCascadeWarning || !isDirty}
            >
              {submitting ? 'saving…' : 'save changes'}
            </button>
          </div>
        </footer>
      </form>
      <EditCascadeWarningModal
        show={showCascadeWarning}
        onCancel={closeCascadeModal}
        onConfirm={performSave}
        sleepScreenId={entry ? entry.sleepScreenId : ''}
        descriptionBefore={entry ? entry.description || '' : ''}
        descriptionAfter={pendingSave ? pendingSave.description : ''}
        videoUrlBefore={entry ? entry.videoUrl || '' : ''}
        videoUrlAfter={pendingSave ? pendingSave.videoUrl : ''}
      />
    </main>
  );
};

export default SleepScreenEdit;
